/* eslint-disable no-debugger */
/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { ILobasProductSelectorData } from './lobas-product-selector.data';
import { ILobasProductSelectorProps } from './lobas-product-selector.props.autogenerated';

import { ITelemetryContent, getTelemetryObject, IModuleProps, isMobile, VariantType } from '@msdyn365-commerce-modules/utilities';
import { ILobasLabeledDropdownOnChangeNotification, ILobasLabeledDropdownOption } from '../components/lobas-labeled-dropdown.props';
//import classnames from 'classnames';
import { getUpdatedRefinementCriteria, IRefineItemToggleNotification } from '../lobas-search-result-container/components';

import { transaction, reaction, observable, computed } from 'mobx';
import { observer } from 'mobx-react';
import { buildListPageUrl, getCurrentUrl } from '../components/lobas-url-utils';
import LobasLabeledDropdown from '../components/lobas-labeled-dropdown';
import MsDyn365 from '@msdyn365-commerce/core';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { GridSize } from '../lobas-search-result-container/lobas-search-result-container';

export interface ILobasProductSelectorViewProps extends ILobasProductSelectorProps<ILobasProductSelectorData> {
    selectorContainer?: React.ReactNode;
    productSelector: IModuleProps;
}

export interface ILobasProductSelectorRefinerHierarchy {
    type: string;
    covering: string;
    manufacturer: string;
    model: string;
}

export interface ILobasProductSelectorState {
    refiners: ILobasProductSelectorRefinerHierarchy;
    isReady: boolean;
    refresh: boolean;
}

/**
 *
 * LobasProductSelector component
 * @extends {React.PureComponent<ILobasProductSelectorProps<ILobasProductSelectorData>>}
 */
@observer
class LobasProductSelector extends React.PureComponent<ILobasProductSelectorProps<ILobasProductSelectorData>, ILobasProductSelectorState> {
    @observable
    private _viewport: GridSize = 'lg';

    @computed get isMobile(): boolean {
        return this._viewport === 'xs' || this._viewport === 'sm';
    }

    public constructor(props: ILobasProductSelectorProps<ILobasProductSelectorData>, state: ILobasProductSelectorState) {
        super(props);
        this._viewport = props.context.request?.device?.Type === 'Mobile' ? 'xs' : 'lg';
        this._updateViewport = this._updateViewport.bind(this);
        this.state = {
            isReady: false,
            refresh: true,
            refiners: { type: '', covering: '', manufacturer: '', model: '' }
        };
    }

    public async componentDidMount(): Promise<void> {
        if (MsDyn365.isBrowser && window.addEventListener) {
            window.addEventListener('resize', this._updateViewport);
            this._updateViewport();
        }

        const listPageState = await this.props.data.listPageState;

        // Initialize reaction based on listPageState properties
        reaction(
            () => {
                return [
                    listPageState.activeFilters?.length,
                    listPageState.currentPageNumber,
                    ArrayExtensions.hasElements(listPageState.sortingCritera?.Columns)
                ];
            },
            async () => {
                const newListPageState = await this.props.data.listPageState;

                if (newListPageState && newListPageState.activeFilters && ArrayExtensions.hasElements(newListPageState.activeFilters)) {
                    const type = newListPageState.activeFilters.filter(
                        refiner => refiner.RefinerRecordId === this.props.config.typeRecordId
                    );
                    const covering = newListPageState.activeFilters.filter(
                        refiner => refiner.RefinerRecordId === this.props.config.coveringRecordId
                    );
                    const manufacturer = newListPageState.activeFilters.filter(
                        refiner => refiner.RefinerRecordId === this.props.config.manufacturerRecordId
                    );
                    const model = newListPageState.activeFilters.filter(
                        refiner => refiner.RefinerRecordId === this.props.config.modelRecordId
                    );

                    const selectedRefinerValues: ILobasProductSelectorRefinerHierarchy = {
                        type: ArrayExtensions.hasElements(type) && type[0].LeftValueBoundString ? type[0].LeftValueBoundString : '',
                        covering:
                            ArrayExtensions.hasElements(covering) && covering[0].LeftValueBoundString
                                ? covering[0].LeftValueBoundString
                                : '',
                        manufacturer:
                            ArrayExtensions.hasElements(manufacturer) && manufacturer[0].LeftValueBoundString
                                ? manufacturer[0].LeftValueBoundString
                                : '',
                        model: ArrayExtensions.hasElements(model) && model[0].LeftValueBoundString ? model[0].LeftValueBoundString : ''
                    };

                    this.setState({ refiners: selectedRefinerValues });
                } else {
                    this.setState({ refiners: { type: '', covering: '', manufacturer: '', model: '' } });
                }
            }
        );
    }

    public render(): JSX.Element | null {
        const viewProps = {
            ...this.props,
            selectorContainer: this.createSelectorContainer(this.props.data),
            productSelector: {
                moduleProps: this.props,
                className: 'lobas-product-selector-container',
                tag: 'div'
            }
        };

        return this.props.renderView(viewProps);
    }

    private createSelectorContainer(content: ILobasProductSelectorData): JSX.Element | null {
        let typeFiltered = content.roofTable.result;
        let coveringFiltered = content.roofTable.result;
        let manufacturerFiltered = content.roofTable.result;
        let modelFiltered = content.roofTable.result;

        typeFiltered = typeFiltered?.filter(roof =>
            this.state.refiners.type !== ''
                ? roof.CoveringName === this.state.refiners.covering
                : true && this.state.refiners.manufacturer !== ''
                ? roof.ManufacturerName === this.state.refiners.manufacturer
                : true && this.state.refiners.model !== ''
                ? roof.ModelName === this.state.refiners.model
                : true
        );

        coveringFiltered = coveringFiltered?.filter(roof =>
            this.state.refiners.type !== ''
                ? roof.TypeName === this.state.refiners.type
                : true && this.state.refiners.manufacturer !== ''
                ? roof.ManufacturerName === this.state.refiners.manufacturer
                : true && this.state.refiners.model !== ''
                ? roof.ModelName === this.state.refiners.model
                : true
        );
        manufacturerFiltered = manufacturerFiltered?.filter(roof =>
            this.state.refiners.type !== ''
                ? roof.TypeName === this.state.refiners.type
                : true && this.state.refiners.covering !== ''
                ? roof.CoveringName === this.state.refiners.covering
                : true && this.state.refiners.model !== ''
                ? roof.ModelName === this.state.refiners.model
                : true
        );
        modelFiltered = modelFiltered?.filter(roof =>
            this.state.refiners.type !== ''
                ? roof.TypeName === this.state.refiners.type
                : true && this.state.refiners.covering !== ''
                ? roof.CoveringName === this.state.refiners.covering
                : true && this.state.refiners.manufacturer !== ''
                ? roof.ManufacturerName === this.state.refiners.manufacturer
                : true
        );

        let distinctTypes = typeFiltered
            ? typeFiltered.map(roof => {
                  return roof.TypeName ? roof.TypeName : '';
              })
            : [];
        let distinctCoverings = coveringFiltered
            ? coveringFiltered.map(roof => {
                  return roof.CoveringName ? roof.CoveringName : '';
              })
            : [];
        let distinctManufacturers = manufacturerFiltered
            ? manufacturerFiltered.map(roof => {
                  return roof.ManufacturerName ? roof.ManufacturerName : '';
              })
            : [];
        let distinctModels = modelFiltered
            ? modelFiltered.map(roof => {
                  return roof.ModelName ? roof.ModelName : '';
              })
            : [];

        distinctTypes = distinctTypes.sort();
        distinctCoverings = distinctCoverings.sort();
        distinctManufacturers = distinctManufacturers.sort();
        distinctModels = distinctModels.sort();

        const typeDropdownItems: ILobasLabeledDropdownOption[] = distinctTypes
            .filter((v, i, a) => a.indexOf(v) === i)
            .map((type, index) => {
                return {
                    value: type,
                    key: index.toString()
                };
            });
        typeDropdownItems.unshift({ value: this.props.resources.defaultValueLabel, key: '' });

        const coveringDropdownItems: ILobasLabeledDropdownOption[] = distinctCoverings
            .filter((v, i, a) => a.indexOf(v) === i)
            .map((type, index) => {
                return {
                    value: type,
                    key: index.toString()
                };
            });
        coveringDropdownItems.unshift({ value: this.props.resources.defaultValueLabel, key: '' });

        const manufacturerDropdownItems: ILobasLabeledDropdownOption[] = distinctManufacturers
            .filter((v, i, a) => a.indexOf(v) === i)
            .map((type, index) => {
                return {
                    value: type,
                    key: index.toString()
                };
            });
        manufacturerDropdownItems.unshift({ value: this.props.resources.defaultValueLabel, key: '' });

        const modelDropdownItems: ILobasLabeledDropdownOption[] = distinctModels
            .filter((v, i, a) => a.indexOf(v) === i)
            .map((type, index) => {
                return {
                    value: type,
                    key: index.toString()
                };
            });

        modelDropdownItems.unshift({ value: this.props.resources.defaultValueLabel, key: '' });

        const typeDropdown = (
            <LobasLabeledDropdown
                dropdownId={'Type'}
                dropdownOptions={typeDropdownItems}
                onSelectOption={this._onChangedType}
                labelText={this.props.resources.typeLabel}
                selectedOption={
                    typeDropdownItems.filter(item => item.value === this.state.refiners.type)[0] !== undefined
                        ? typeDropdownItems.filter(item => item.value === this.state.refiners.type)[0]
                        : typeDropdownItems[0]
                }
                controlledByParent={true}
                dropdownDisabled={false}
            />
        );
        const coveringDropdown = (
            <LobasLabeledDropdown
                dropdownId={'Covering'}
                labelText={this.props.resources.coveringLabel}
                dropdownOptions={coveringDropdownItems}
                onSelectOption={this._onChangedCovering}
                selectedOption={
                    coveringDropdownItems.filter(item => item.value === this.state.refiners.covering)[0] !== undefined
                        ? coveringDropdownItems.filter(item => item.value === this.state.refiners.covering)[0]
                        : coveringDropdownItems[0]
                }
                controlledByParent={true}
                dropdownDisabled={false /* this.state.refiners.type !== '' && this.state.refiners.type !== undefined ? false : true */}
            />
        );
        const manufacturerDropdown = (
            <LobasLabeledDropdown
                dropdownId={'Manufacturer'}
                labelText={this.props.resources.manufacturerLabel}
                dropdownOptions={manufacturerDropdownItems}
                onSelectOption={this._onChangedManufacturer}
                selectedOption={
                    manufacturerDropdownItems.filter(item => item.value === this.state.refiners.manufacturer)[0] !== undefined
                        ? manufacturerDropdownItems.filter(item => item.value === this.state.refiners.manufacturer)[0]
                        : manufacturerDropdownItems[0]
                }
                controlledByParent={true}
                dropdownDisabled={
                    false /* this.state.refiners.covering !== '' && this.state.refiners.covering !== undefined ? false : true */
                }
            />
        );
        const modelDropdown = (
            <LobasLabeledDropdown
                dropdownId={'Model'}
                labelText={this.props.resources.modelLabel}
                dropdownOptions={modelDropdownItems}
                onSelectOption={this._onChangedModel}
                selectedOption={
                    modelDropdownItems.filter(item => item.value === this.state.refiners.model)[0] !== undefined
                        ? modelDropdownItems.filter(item => item.value === this.state.refiners.model)[0]
                        : modelDropdownItems[0]
                }
                controlledByParent={true}
                dropdownDisabled={
                    false /* this.state.refiners.manufacturer !== '' && this.state.refiners.manufacturer !== undefined ? false : true */
                }
            />
        );

        return (
            <div className={'lobas-dropdown'}>
                <div className='lobas-dropdown-group'>{typeDropdown}</div>
                <div className='lobas-dropdown-group'>{coveringDropdown}</div>
                <div className='lobas-dropdown-group'>{manufacturerDropdown}</div>
                <div className='lobas-dropdown-group'>{modelDropdown}</div>
            </div>
        );
    }

    private readonly _onChangedType = (notification: ILobasLabeledDropdownOnChangeNotification) => {
        this.updateRefiners(notification, 'Type');
    };

    private readonly _onChangedCovering = (notification: ILobasLabeledDropdownOnChangeNotification) => {
        this.updateRefiners(notification, 'Covering');
    };

    private readonly _onChangedManufacturer = (notification: ILobasLabeledDropdownOnChangeNotification) => {
        this.updateRefiners(notification, 'Manufacturer');
    };

    private readonly _onChangedModel = (notification: ILobasLabeledDropdownOnChangeNotification) => {
        this.updateRefiners(notification, 'Model');
    };

    private updateRefiners(notification: ILobasLabeledDropdownOnChangeNotification, id: string) {
        const { context } = this.props;
        const requestContext = context && context.request;
        const actionContext = context && context.actionContext;

        const typeRecordId = this.props.config.typeRecordId;
        const coveringRecordId = this.props.config.coveringRecordId;
        const manufacturerRecordId = this.props.config.manufacturerRecordId;
        const modelRecordId = this.props.config.modelRecordId;

        let typeChanged = false;
        let coveringChanged = false;
        let manufacturerChanged = false;

        if (!requestContext || !actionContext || !this.props.data.listPageState.result) {
            const error = `Refine menu cannot refine search criteria: ${!requestContext ? 'requestContext ' : ''} ${
                !actionContext ? 'actionContext ' : ''
            } could not be found`;
            this.props.telemetry.warning(error);
        }

        let refinerRecordId: number | undefined;

        switch (id) {
            case 'Type':
                refinerRecordId = typeRecordId;
                typeChanged = true;
                break;
            case 'Covering':
                refinerRecordId = coveringRecordId;
                coveringChanged = true;
                break;
            case 'Manufacturer':
                refinerRecordId = manufacturerRecordId;
                manufacturerChanged = true;
                break;
            case 'Model':
                refinerRecordId = modelRecordId;
                break;
        }

        const refineNotification: IRefineItemToggleNotification = {
            productRefinerValue: {
                RefinerSourceValue: 1,
                RefinerRecordId: refinerRecordId,
                LeftValueBoundString: notification.selectedOption.value,
                RightValueBoundString: notification.selectedOption.value
            },
            parentProductRefinerHierarchy: { RecordId: refinerRecordId, RefinerTypeValue: 1, Values: [] },
            isSelecting: false
        };

        let updatedRefinementCriteria = (this.props.data.listPageState.result && this.props.data.listPageState.result.activeFilters) || [];
        let refinementCriteriaUpdated = false;

        if (typeChanged) {
            updatedRefinementCriteria = updatedRefinementCriteria
                ? updatedRefinementCriteria.filter(
                      filter =>
                          filter.RefinerRecordId !== coveringRecordId &&
                          filter.RefinerRecordId !== manufacturerRecordId &&
                          filter.RefinerRecordId !== modelRecordId
                  )
                : [];
        } else if (coveringChanged) {
            updatedRefinementCriteria = updatedRefinementCriteria
                ? updatedRefinementCriteria.filter(
                      filter => filter.RefinerRecordId !== manufacturerRecordId && filter.RefinerRecordId !== modelRecordId
                  )
                : [];
        } else if (manufacturerChanged) {
            updatedRefinementCriteria = updatedRefinementCriteria
                ? updatedRefinementCriteria.filter(filter => filter.RefinerRecordId !== modelRecordId)
                : [];
        }

        if (refineNotification.productRefinerValue.LeftValueBoundString === '') {
            updatedRefinementCriteria = updatedRefinementCriteria
                ? updatedRefinementCriteria.filter(
                      filter => filter.RefinerRecordId !== refineNotification.productRefinerValue.RefinerRecordId
                  )
                : [];
            refinementCriteriaUpdated = true;
        } else {
            updatedRefinementCriteria = updatedRefinementCriteria
                ? updatedRefinementCriteria.map(refiner => {
                      if (refiner.RefinerRecordId === refineNotification.productRefinerValue.RefinerRecordId) {
                          refiner.LeftValueBoundString = refineNotification.productRefinerValue.LeftValueBoundString;
                          refiner.RightValueBoundString = refineNotification.productRefinerValue.RightValueBoundString;
                          refinementCriteriaUpdated = true;
                      }

                      return refiner;
                  })
                : [];
        }

        updatedRefinementCriteria = updatedRefinementCriteria.filter(
            refiner => refiner.LeftValueBoundString !== this.props.resources.defaultValueLabel
        );

        if (!refinementCriteriaUpdated) {
            updatedRefinementCriteria = getUpdatedRefinementCriteria(refineNotification, updatedRefinementCriteria);
        }

        const selectedType =
            updatedRefinementCriteria && updatedRefinementCriteria[0] && updatedRefinementCriteria[0].LeftValueBoundString
                ? updatedRefinementCriteria[0].LeftValueBoundString
                : '';

        let selectedCovering;
        let selectedManufacturer;
        let selectedModel;

        if (updatedRefinementCriteria && updatedRefinementCriteria[1] && updatedRefinementCriteria[1].LeftValueBoundString) {
            if (typeChanged) {
                selectedCovering = '';
            } else {
                selectedCovering = updatedRefinementCriteria[1].LeftValueBoundString;
            }
        } else {
            selectedCovering = '';
        }
        if (updatedRefinementCriteria && updatedRefinementCriteria[2] && updatedRefinementCriteria[2].LeftValueBoundString) {
            if (typeChanged || coveringChanged) {
                selectedManufacturer = '';
            } else {
                selectedManufacturer = updatedRefinementCriteria[2].LeftValueBoundString;
            }
        } else {
            selectedManufacturer = '';
        }
        if (updatedRefinementCriteria && updatedRefinementCriteria[3] && updatedRefinementCriteria[3].LeftValueBoundString) {
            if (typeChanged || coveringChanged || manufacturerChanged) {
                selectedModel = '';
            } else {
                selectedModel = updatedRefinementCriteria[3].LeftValueBoundString;
            }
        } else {
            selectedModel = '';
        }

        const selectedRefinerValues: ILobasProductSelectorRefinerHierarchy = {
            type: selectedType,
            covering: selectedCovering,
            manufacturer: selectedManufacturer,
            model: selectedModel
        };

        this.setState({ refiners: selectedRefinerValues });

        window.history.pushState({}, '', buildListPageUrl(getCurrentUrl(this.props.context.request), updatedRefinementCriteria));
        transaction(() => {
            this.props.data.listPageState.result!.currentPageNumber = 0;
            this.props.data.listPageState.result!.activeFilters = updatedRefinementCriteria;
        });
    }

    private _updateViewport(): void {
        this._viewport = this._getViewport();
    }

    private _getViewport(): GridSize {
        if (MsDyn365.isBrowser) {
            return isMobile({ variant: VariantType.Browser, context: this.props.context.request });
        }
        return isMobile({ variant: VariantType.Viewport, context: this.props.context.request });
    }
}

export function getTelemetryContent(props: ILobasProductSelectorProps<ILobasProductSelectorData>): ITelemetryContent {
    return getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);
}

export default LobasProductSelector;
